import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Box,
    Card,
    Typography,
    Grid,
    Avatar,
    Button,
    styled,
    useTheme,
    useMediaQuery,
    Fab,
    Zoom,
    Tooltip,
    Stack,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper
} from "@mui/material";

/* ✅ ProDev Assets & Icons */
import { AiFillBulb, AiOutlineRead } from "react-icons/ai";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CodeIcon from "@mui/icons-material/Code";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import StopIcon from "@mui/icons-material/Stop";

const bgImage = "https://images.unsplash.com/photo-1555066931-4365d14bab8c";

/* ✅ Styled Components - ProDev Dark Glass Theme */
const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.9), rgba(16,7,32,0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(16,7,32,0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    marginBottom: "25px",
    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "white",
}));

const CodeBlock = styled("pre")(({ theme }) => ({
    backgroundColor: "#0d1117",
    color: "#c9d1d9",
    padding: "20px",
    borderRadius: "10px",
    overflowX: "auto",
    fontSize: "0.9rem",
    lineHeight: 1.6,
    border: "1px solid rgba(255,255,255,0.05)",
    fontFamily: "'Fira Code', 'Courier New', monospace",
}));

const ActionButton = styled(Button)(({ theme }) => ({
    backgroundColor: "goldenrod",
    color: "#100720",
    fontWeight: 800,
    padding: "12px 40px",
    borderRadius: "999px",
    transition: "0.3s",
    "&:hover": { backgroundColor: "#e0b200", transform: "scale(1.05)" },
}));

/**
 * MapDemo Component
 * Specialized ProDevConsult Module
 * Author: Hazzan
 */
export default function MapDemo() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    /* ✅ ProDev Local Data */
    const localData = [
        { id: 1, name: "Hazzan", age: 30, profession: "Lead Architect" },
        { id: 2, name: "Alice Smith", age: 25, profession: "UI Designer" },
        { id: 3, name: "Michael Lee", age: 35, profession: "Backend Dev" },
        { id: 4, name: "Sarah Brown", age: 28, profession: "Marketing" }
    ];

    /* ✅ State Management */
    const [selectedPerson, setSelectedPerson] = useState(null);
    const [apiData, setApiData] = useState([]);
    const [showScroll, setShowScroll] = useState(false);
    const [copiedIndex, setCopiedIndex] = useState(null);
    const [isSpeaking, setIsSpeaking] = useState(false);

    /* ✅ Lifecycle: API Fetching */
    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await fetch("https://jsonplaceholder.typicode.com/users");
                const data = await response.json();
                setApiData(data.slice(0, 4));
            } catch (error) {
                console.error("ProDev API Error:", error);
            }
        };
        fetchData();

        const checkScroll = () => setShowScroll(window.pageYOffset > 400);
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    const handleSpeak = (text) => {
        window.speechSynthesis.cancel();
        if (isSpeaking) { setIsSpeaking(false); return; }
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.onstart = () => setIsSpeaking(true);
        utterance.onend = () => setIsSpeaking(false);
        window.speechSynthesis.speak(utterance);
    };

    const handleCopy = (text, index) => {
        navigator.clipboard.writeText(text);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    const mapModules = useMemo(() => [
        {
            title: "1. Mapping Local Data into Cards",
            tag: "Dynamic UI",
            desc: "The .map() method creates a new array by calling a function for every element. We use the 'key' prop to help React track which items have changed.",
            code: `localData.map(person => (\n  <Card key={person.id}>\n    <h3>{person.name}</h3>\n    <p>{person.profession}</p>\n  </Card>\n));`
        },
        {
            title: "2. Tabular Data Mapping",
            tag: "Data Visualization",
            desc: "For structured data, mapping into a Table is ideal. Each iteration generates a TableRow, keeping your code DRY and maintainable.",
            code: `<TableBody>\n  {localData.map(person => (\n    <TableRow key={person.id}>\n      <TableCell>{person.name}</TableCell>\n    </TableRow>\n  ))}\n</TableBody>`
        },
        {
            title: "3. Live API Data Integration",
            tag: "Async Rendering",
            desc: "At ProDevConsult, we fetch remote data inside useEffect and map the result to the UI once the promise resolves. This is the heart of modern web apps.",
            code: `useEffect(() => {\n  const data = await fetchUsers();\n  setApiData(data);\n}, []);\n\n{apiData.map(user => <UserCard key={user.id} />)}`
        }
    ], []);

    return (
        <PageWrapper>
            <Box sx={{ textAlign: "center", mb: 6, px: 2 }}>
                <Typography variant={isMobile ? "h4" : "h3"} sx={{
                    fontWeight: 800, backgroundColor: "gold", color: "#100720",
                    px: { xs: 3, md: 6 }, py: 1.5, borderRadius: "999px",
                    display: "inline-block", boxShadow: "0 6px 20px rgba(218, 165, 32, 0.3)",
                }}>
                    REACT MAPPING PATH
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                <Grid item xs={12}>
                    <SectionCard>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                            <Typography variant="h5" fontWeight={800} color="goldenrod">
                                Mastering Array.map()
                            </Typography>
                            <IconButton onClick={() => handleSpeak("In this module, Hazzan demonstrates how to map local and API data into dynamic UI components.")} sx={{ color: "goldenrod" }}>
                                <VolumeUpIcon />
                            </IconButton>
                        </Stack>
                        <Typography variant="body1" sx={{ opacity: 0.9, lineHeight: 1.7 }}>
                            Data-driven development relies on transforming arrays of objects into visual components. Whether rendering cards, tables, or lists, the <b>.map()</b> function is your primary tool for building scalable React interfaces.
                        </Typography>
                    </SectionCard>
                </Grid>

                {/* ✅ Mapping Content Cards */}
                {mapModules.map((item, i) => (
                    <Grid item xs={12} key={i}>
                        <SectionCard>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                    <Avatar sx={{ bgcolor: "rgba(218, 165, 32, 0.2)", color: "goldenrod" }}><AiOutlineRead /></Avatar>
                                    <Box>
                                        <Typography variant="h6" fontWeight={700}>{item.title}</Typography>
                                        <Typography variant="caption" color="goldenrod" fontWeight={800}>{item.tag}</Typography>
                                    </Box>
                                </Box>
                                <Tooltip title={copiedIndex === i ? "Copied!" : "Copy Code"}>
                                    <IconButton onClick={() => handleCopy(item.code, i)} sx={{ color: "white" }}>
                                        {copiedIndex === i ? <CheckIcon /> : <ContentCopyIcon />}
                                    </IconButton>
                                </Tooltip>
                            </Box>

                            <Typography variant="body1" sx={{ mb: 2, opacity: 0.9 }}>{item.desc}</Typography>
                            <CodeBlock>{item.code}</CodeBlock>

                            {/* Live Demo Rendering */}
                            <Box sx={{ mt: 4 }}>
                                {i === 0 && (
                                    <Grid container spacing={2}>
                                        {localData.map(person => (
                                            <Grid item xs={12} sm={6} md={3} key={person.id}>
                                                <Card sx={{ p: 2, bgcolor: "rgba(255,255,255,0.05)", borderLeft: "4px solid goldenrod", cursor: "pointer", "&:hover": { bgcolor: "rgba(255,255,255,0.1)" } }} onClick={() => setSelectedPerson(person)}>
                                                    <Typography variant="subtitle1" fontWeight="bold" color="white">{person.name}</Typography>
                                                    <Typography variant="caption" color="goldenrod">{person.profession}</Typography>
                                                </Card>
                                            </Grid>
                                        ))}
                                    </Grid>
                                )}

                                {i === 1 && (
                                    <TableContainer component={Paper} sx={{ backgroundColor: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.1)" }}>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ color: "goldenrod", fontWeight: "bold" }}>Name</TableCell>
                                                    <TableCell sx={{ color: "goldenrod", fontWeight: "bold" }}>Age</TableCell>
                                                    <TableCell sx={{ color: "goldenrod", fontWeight: "bold" }}>Profession</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {localData.map(person => (
                                                    <TableRow key={person.id}>
                                                        <TableCell sx={{ color: "white" }}>{person.name}</TableCell>
                                                        <TableCell sx={{ color: "white" }}>{person.age}</TableCell>
                                                        <TableCell sx={{ color: "white" }}>{person.profession}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}

                                {i === 2 && (
                                    <Grid container spacing={2}>
                                        {apiData.map(user => (
                                            <Grid item xs={12} sm={6} md={3} key={user.id}>
                                                <Card sx={{ p: 2, height: "100%", bgcolor: "rgba(218, 165, 32, 0.05)", border: "1px solid rgba(218, 165, 32, 0.2)" }}>
                                                    <Typography variant="subtitle2" fontWeight="bold" color="white">{user.name}</Typography>
                                                    <Typography variant="caption" display="block" color="rgba(255,255,255,0.6)">{user.email}</Typography>
                                                    <Typography variant="caption" color="goldenrod">{user.company.name}</Typography>
                                                </Card>
                                            </Grid>
                                        ))}
                                    </Grid>
                                )}
                            </Box>
                        </SectionCard>
                    </Grid>
                ))}

                <Grid item xs={12} textAlign="center" sx={{ mt: 4, mb: 10 }}>
                    <Link to="/javascriptcoding" style={{ textDecoration: "none" }}>
                        <ActionButton variant="contained">PROCEED TO NEXT MODULE</ActionButton>
                    </Link>
                </Grid>
            </Grid>

            {/* ✅ Floating Controls */}
            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                {isSpeaking && (
                    <Zoom in={isSpeaking}>
                        <Fab size="medium" color="error" onClick={() => window.speechSynthesis.cancel()}><StopIcon /></Fab>
                    </Zoom>
                )}
                <Zoom in={showScroll}>
                    <Fab size="small" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} sx={{ bgcolor: "goldenrod" }}>
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </PageWrapper>
    );
}
